import { native, type IAPServiceMethods } from '@forest/native-services'
import { createQuery } from '@tanstack/solid-query'
import type { BoostingPack } from '~/config'

type GetPriceResult = Awaited<ReturnType<IAPServiceMethods['getPrice']>>

/**
 * Picks the store-localized price string from a native `getPrice` result,
 * falling back to a hard-coded label when the price is unavailable (failure
 * result, or running in an environment without the bridge). While the query
 * is still pending the consumer should show a loading state instead of
 * calling this with `undefined` — otherwise the USD fallback flashes before
 * the localized price arrives.
 */
export function selectPriceLabel(
  result: GetPriceResult | undefined,
  fallback: string,
): string {
  if (result?.success) {
    return result.data.price_localized
  }
  return fallback
}

/**
 * Fetches the store-localized price for a pack via the native bridge. On error
 * the consumer falls back to `pack.fallbackPrice` through {@link selectPriceLabel}.
 */
export const useBoostingPackPrice = (pack: BoostingPack) =>
  createQuery(() => ({
    queryKey: ['boosting-pack-price', pack.productId],
    queryFn: () =>
      native.iap.getPrice({
        product_id: pack.productId,
        product_type: 'consumable',
        sku: pack.sku,
      }),
    staleTime: Infinity,
  }))
